import { createSlice } from "@reduxjs/toolkit";
import {
  get_user_chatrooms_thunk,
  get_chatroom_by_id_thunk,
  get_unread_count_thunk,
  update_chatroom_status_thunk,
} from "./chatroomThunk";

const initialState = {
  chatRooms: [],
  currentChatRoom: null,
  unreadCounts: {
    totalRooms: 0,
    roomIds: []
  },
  loading: false,
  statusUpdating: false,
  error: null,
};

const chatroomSlice = createSlice({
  name: "chatroom",
  initialState,
  reducers: {
    setChatRooms: (state, action) => {
      state.chatRooms = action.payload;
      state.loading = false;
      state.error = null;
    },
    setCurrentChatRoom: (state, action) => {
      state.currentChatRoom = action.payload;
      state.loading = false;
      state.error = null;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    clearChatRoomState: (state) => {
      state.currentChatRoom = null;
      state.error = null;
    },
    updateActiveChatRoomSettings: (state, action) => {
      const { roomId, settings } = action.payload;
      
      // Helper function to handle deep merging of settings
      const mergeSettings = (existingSettings, newSettings) => {
        const result = { ...existingSettings };
        
        // Process each setting key in the new settings
        Object.entries(newSettings).forEach(([key, value]) => {
          // Handle structured settings (objects with enabled and options)
          if (value && typeof value === 'object') {
            // Handle settings with 'enabled' property (new format)
            if ('enabled' in value) {
              // If existing setting exists and has the same structure, merge them
              if (result[key] && typeof result[key] === 'object' && 'enabled' in result[key]) {
                result[key] = {
                  ...result[key],
                  enabled: value.enabled,
                  options: { ...(result[key].options || {}), ...(value.options || {}) }
                };
              } else {
                // Otherwise, use the new value directly
                result[key] = value;
              }
              
              // Special case for check_in_interval and disable_speak to ensure proper format
              if (key === 'check_in_interval' || key === 'disable_speak') {
                
                // For check_in_interval, ensure we have the actual numeric value too
                if (key === 'check_in_interval' && value.options && value.options.interval) {
                  // Keep the structured version but also set the direct value for backward compatibility
                  result[key] = value.enabled ? value.options.interval : 0;
                }
                
                // For disable_speak, ensure we have the boolean value too
                if (key === 'disable_speak') {
                  // Keep the structured version but also set the direct value for backward compatibility
                  result[key] = value.enabled;
                }
              }
            } else {
              // For other objects without 'enabled' property, just do a shallow merge
              result[key] = { ...(result[key] || {}), ...value };
            }
          } else {
            // For legacy settings (direct values), just replace them
            result[key] = value;
            
            // Special case for enabling/converting legacy values to structured format
            if (key === 'check_in_interval' && typeof value === 'number') {
              // Create structured version alongside legacy format
              result[key] = {
                enabled: value > 0,
                options: { interval: value }
              };
            }
            
            if (key === 'disable_speak' && typeof value === 'boolean') {
              // Create structured version alongside legacy format
              result[key] = {
                enabled: value,
                options: {}
              };
            }
          }
        });
        
        return result;
      };
      
      // Find the chat room in the chat rooms list and update its settings
      const chatRoomIndex = state.chatRooms.findIndex(
        (chatRoom) => chatRoom._id === roomId
      );
      
      if (chatRoomIndex !== -1) {
        const chatRoom = state.chatRooms[chatRoomIndex];
        state.chatRooms[chatRoomIndex] = {
          ...chatRoom,
          settings: mergeSettings(chatRoom.settings || {}, settings)
        };
      }
      
      // Update the current chat room if it's the active one
      if (state.currentChatRoom && state.currentChatRoom._id === roomId) {
        state.currentChatRoom = {
          ...state.currentChatRoom,
          settings: mergeSettings(state.currentChatRoom.settings || {}, settings)
        };
      }
    },
    updateChatRoomMediaLink: (state, action) => {
      const { roomId, mediaType, link } = action.payload;
      const linkField = `${mediaType}_link`; // video_link or document_link
      
      // Update the media link in all chat rooms
      state.chatRooms = state.chatRooms.map(room => 
        room._id === roomId 
          ? { ...room, [linkField]: link } 
          : room
      );
      
      // Update current chat room if it matches
      if (state.currentChatRoom && state.currentChatRoom._id === roomId) {
        state.currentChatRoom = {
          ...state.currentChatRoom,
          [linkField]: link
        };
      }
    },
  },
  extraReducers: (builder) => {
    // Get user chat rooms cases
    builder
      .addCase(get_user_chatrooms_thunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(get_user_chatrooms_thunk.fulfilled, (state, action) => {
        state.chatRooms = action.payload.data;
        state.loading = false;
      })
      .addCase(get_user_chatrooms_thunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Get chat room by ID cases
    builder
      .addCase(get_chatroom_by_id_thunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(get_chatroom_by_id_thunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentChatRoom = action.payload.data;
      })
      .addCase(get_chatroom_by_id_thunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Get unread count cases
    builder
      .addCase(get_unread_count_thunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(get_unread_count_thunk.fulfilled, (state, action) => {
        state.loading = false;
        state.unreadCounts = action.payload.data;
      })
      .addCase(get_unread_count_thunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
      
    // Update chat room status cases
    builder
      .addCase(update_chatroom_status_thunk.pending, (state) => {
        state.statusUpdating = true;
        state.error = null;
      })
      .addCase(update_chatroom_status_thunk.fulfilled, (state, action) => {
        state.statusUpdating = false;
        // Update current chat room if it matches the updated one
        if (state.currentChatRoom && state.currentChatRoom._id === action.payload.data._id) {
          state.currentChatRoom = action.payload.data;
        }
        // Update chat room in the list
        state.chatRooms = state.chatRooms.map(room => 
          room._id === action.payload.data._id ? action.payload.data : room
        );
      })
      .addCase(update_chatroom_status_thunk.rejected, (state, action) => {
        state.statusUpdating = false;
        state.error = action.payload;
      });
  },
});

export const {
  setChatRooms,
  setCurrentChatRoom,
  setLoading,
  setError,
  clearChatRoomState,
  updateActiveChatRoomSettings,
  updateChatRoomMediaLink,
} = chatroomSlice.actions;

export default chatroomSlice.reducer; 